/* tslint:disable:triple-equals */

import { Injectable } from '@angular/core';
import { ReplaySubject, Observable, Subject, of } from 'rxjs';
import { take, switchMap, map } from 'rxjs/operators';

import { Datastore } from '@services/datastore';
import { MemberService } from '@services/member.service';
import { FeatureFlag } from '@models/feature-flag/feature-flag.model';
import { JsonApiQueryData } from '@elearnio/angular2-jsonapi';
import { FEATURES } from '@core/constants/features.constant';
import { Roles } from '@core/constants/roles.constant';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  private featureFlags: FeatureFlag[] = [];
  private isLoading = false;
  private concurrentRequestSubject = new Subject<FeatureFlag[]>();

  constructor(
    private datastore: Datastore,
    private memberService: MemberService,
  ) {}

  isFeatureFlagEnabled(key: string): Observable<boolean> {
    const subject = new ReplaySubject<boolean>(1);

    this.getFeatureFlags()
      .pipe(take(1))
      .subscribe(featureFlags => {
        subject.next(!!featureFlags.find(flag => flag.key === key)?.enabled);
      });

    return subject.asObservable();
  }

  isPerformanceHidden(): Observable<boolean> {
    return this.isFeatureFlagEnabled(FEATURES.HIDE_PERFORMANCE_DATA).pipe(
      take(1),
      switchMap(isEnabled => {
        if (!isEnabled) return of(false);
        return this.memberService.current().pipe(
          take(1),
          map(member => member?.role !== Roles.ADMIN),
        );
      }),
    );
  }

  private getFeatureFlags(): Observable<FeatureFlag[]> {
    const subject = new ReplaySubject<FeatureFlag[]>(1);

    if (this.isLoading) {
      return this.concurrentRequestSubject.asObservable();
    }

    if (!this.featureFlags.length) {
      this.isLoading = true;

      this.datastore
        .findAll(FeatureFlag)
        .pipe(take(1))
        .subscribe((featureFlags: JsonApiQueryData<FeatureFlag>) => {
          this.featureFlags = featureFlags.getModels();
          this.concurrentRequestSubject.next(this.featureFlags);
          this.isLoading = false;
          subject.next(this.featureFlags);
        });
    } else {
      subject.next(this.featureFlags);
    }

    return subject;
  }
}
